import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductService } from '../shared/product.service';
import { Edit, LucideAngularModule, Plus, Save, Trash } from 'lucide-angular';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProductModel } from '../shared/product.model';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { ProductVariantModel } from '../shared/product-variant.model';
import { Column } from '@app/partials/datatable/data-table/column';
import { ProductVariantOptionsComponent } from './product-variant-options.component';
import { ProductVariantService } from '../shared/product-variant.service';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { RowAction } from '@app/partials/datatable/data-table/row-action';
import { ProductVariantNameComponent } from '@app/products/edit/product-variant-name.component';
import { NotificationService } from '@app/shared/notification/notification.service';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';

@Component({
  selector: 'app-edit',
  imports: [
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    TextInputComponent,
    SelectComponent,
    DataTableComponent,
    LucideAngularModule,
    NgxEditorModule,
    ModalComponent,
    CheckboxComponent,
  ],
  templateUrl: './product-edit.component.html',
  styleUrl: './product-edit.component.css',
})
export class ProductEditComponent extends AbstractEntityEditComponent implements OnInit, OnDestroy {
  protected processing = false;
  protected validationErrors: string[] = [];
  protected product!: ProductModel;

  protected readonly SaveIcon = Save;
  protected readonly PlusIcon = Plus;
  protected readonly EditIcon = Edit;

  protected variantNameControl: FormControl<string | null> = new FormControl('', [Validators.maxLength(255)]);
  protected variantsPage?: PagedEntities<ProductVariantModel>;
  protected variantsColumns: Column<ProductVariantModel>[] = [
    { field: 'name', header: 'Variant Name', width: '25%', component: ProductVariantNameComponent },
    { field: 'options', header: 'Variant Options', component: ProductVariantOptionsComponent },
  ];

  variantToDelete?: ProductVariantModel;
  variantsRowActions: RowAction<ProductVariantModel>[] = [
    {
      icon: Trash,
      classes: 'text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500',
      callback: (variant) => {
        if (variant.id) {
          this.deleteVariant(variant);
        }
      },
    },
  ];

  protected categoryChoices = [
    { value: 'Dining', label: 'Dining' },
    { value: 'Beverages', label: 'Beverages' },
    { value: 'Accommodation', label: 'Accommodation' },
    { value: 'Add-on', label: 'Add-on' },
    { value: 'Supplier', label: 'Supplier' },
    { value: 'Expense', label: 'Expense' },
    { value: 'Service', label: 'Service' },
  ];

  statusChoices = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
  ];

  destinedForChoices = [
    { value: 'Adults', label: 'Adults' },
    { value: 'Children', label: 'Children' },
    { value: 'Toddler', label: 'Toddler' },
  ];

  imageUrl: string | ArrayBuffer | null = null;
  imageFile: File | null = null;

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    description: new FormControl('', Validators.required),
    price: new FormControl('', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]),
    in_stock: new FormControl(true, Validators.required),
    status: new FormControl('', [Validators.required, Validators.pattern(/^(Active|Inactive)$/)]),
    category: new FormControl('', [
      Validators.required,
      Validators.pattern(/^(Dining|Beverages|Accommodation|Add-on|Supplier|Expense|Service)$/),
    ]),
    destined_for: new FormControl('', [Validators.required, Validators.pattern(/^(Adults|Children|Toddler)$/)]),
    is_minimum_spend: new FormControl(false, Validators.required),
  });

  editor?: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list', 'link'],
    ['text_color', 'background_color'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['undo', 'redo'],
  ];

  constructor(
    private productService: ProductService,
    private variantService: ProductVariantService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
  ) {
    super();
    this.form.valueChanges.subscribe((value) => {
      if (this.form.dirty && !this.form.valid) {
        this.validationErrors = this.getFormValidationErrors(this.form);
      }
    });

    if (this.route.snapshot.data['product']) {
      this.setProduct(this.route.snapshot.data['product']);
    } else {
      const product = {} as ProductModel;
      product.images = [];
      product.status = 'Active';
      product.destined_for = 'Adults';
      product.price_can_be_changed = true;
      product.in_stock = true;
      this.setProduct(product);
    }
  }

  ngOnInit(): void {
    this.editor = new Editor({
      history: true,
      keyboardShortcuts: true,
    });
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  save() {
    if (this.form.invalid) {
      return;
    }
    this.processing = true;

    const formData = new FormData();
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        formData.append(key, control.value);
      }
    });
    if (this.imageFile) {
      formData.append('image', this.imageFile);
    }

    this.productService.saveFormData(formData, this.product?.id).subscribe({
      next: (res) => {
        this.processing = false;
        this.setProduct(res.data);
        this.router.navigate(['/products/' + this.product.id + '/edit']);
        this.form.markAsPristine();
        this.notificationService.success('Product saved successfully');
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
        this.notificationService.error('Failed to save product');
      },
    });
  }

  cancel() {
    return this.router.navigate(['/products']);
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      const reader = new FileReader();
      this.imageFile = file;

      reader.onload = () => {
        this.imageUrl = reader.result;
        this.form.markAsDirty();
      };

      reader.readAsDataURL(file);
    }
  }

  private setProduct(product: any) {
    this.product = product;
    this.variantsPage = { data: product.variants } as PagedEntities<ProductVariantModel>;
    if (!this.product.price_can_be_changed) this.form.get('price')?.disable();
    this.form.patchValue(this.product as any);
  }

  addVariant() {
    if (this.variantNameControl.invalid) {
      return;
    }
    this.variantService
      .save({
        name: this.variantNameControl.value,
        product_id: this.product.id,
      } as unknown as ProductVariantModel)
      .subscribe({
        next: (res) => {
          // add the new variant to the variants list as first item
          this.variantsPage?.data.unshift(res.data);
          this.variantNameControl.reset();
        },
        error: (res) => {
          this.variantNameControl.setErrors({ server: res.error.message });
        },
      });
  }

  deleteVariant(variant: ProductVariantModel) {
    this.variantToDelete = variant;
  }

  confirmDelete() {
    if (this.variantToDelete && this.variantToDelete.id) {
      this.variantService.delete(this.variantToDelete.id.toString()).subscribe({
        next: () => {
          if (this.variantsPage) {
            this.variantsPage.data = this.variantsPage.data.filter(
              (variant) => variant.id !== this.variantToDelete?.id,
            );
          }
          this.variantToDelete = undefined;
        },
        error: (res) => {
          console.error('Error deleting variant:', res);
        },
      });
    }
  }
}
